import { useEffect, useRef, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { toast } from '../Toast/toastStore';
import { t } from '../../i18n/i18n';
import { lowerLabel } from '../CopyButton/CopyButton';

/** How long the pill stays on "Copied" before going back. */
const HELD_FOR = 1000;

/**
 * "Copy" as the same small gradient pill that Show, Set and Open use, for the
 * value rows inside an `InfoPanel`.
 *
 * Not the app's `CopyButton`, which is a 20px glyph: that one belongs beside a
 * login id in a table or a card header, where a labelled pill would shout. Here
 * it would be the only icon in a row of four words, and the row reads as a set
 * of actions — so it takes the word too.
 *
 * On a successful copy it turns into a green "Copied" chip for a second and
 * then goes back, *and* raises the same toast `CopyButton` does. The two are
 * not redundant in practice: the chip answers where you are looking, and the
 * toast is what every other copy in the app does — a copy here reporting
 * itself differently from a copy on Account Metrics would be the odder thing.
 * The message is `CopyButton`'s word for word, so the two are indistinguishable.
 */
export default function CopyPill({ value, label = 'Value' }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);

  // Cleared on unmount so a pill copied just before a route change cannot set
  // state on something that has gone.
  useEffect(() => () => clearTimeout(timer.current), []);

  const onCopy = (_text, ok) => {
    if (!ok) {
      toast.error(t('Could not copy {{label}}', { label: lowerLabel(label) }));
      return;
    }

    toast.success(t('{{label}} copied', { label: t(label) }));
    setCopied(true);
    // Restarted rather than stacked, so copying twice in a row holds the
    // confirmation for a second from the *second* click.
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), HELD_FOR);
  };

  return (
    <CopyToClipboard text={String(value)} onCopy={onCopy}>
      <button
        type="button"
        className={`ip-mini${copied ? ' is-copied' : ''}`}
        // The label stays constant while the visible text changes, so a screen
        // reader announces the state once rather than renaming the control.
        aria-label={t('Copy {{label}}', { label: lowerLabel(label) })}
      >
        {copied ? t('Copied') : t('Copy')}
      </button>
    </CopyToClipboard>
  );
}
